import * as React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';

const Styled = {
  Dialog: styled(Dialog)({
    '& .MuiDialog-paper': {
      borderRadius: '1em',
      maxWidth: '560px',
      width: '100%',
      margin: '1em',
      overflow: 'hidden',
      boxShadow: '0 0.5em 2em rgba(0, 0, 0, 0.12)',
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
  }),
  Header: styled(Box)({
    background: '#357ABD',
    color: '#FFFFFF',
    padding: '2em 1em',
    position: 'relative',
    textAlign: 'center',
  }),
  CloseButton: styled(IconButton)({
    position: 'absolute',
    top: '20px',
    right: '20px',
    color: '#FFFFFF',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    width: '40px',
    height: '40px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  }),
  HeaderTitle: styled(Typography)(({ theme }) => ({
    fontSize: (theme as Theme).typography.EC_TYPE_3XL.fontSize,
    lineHeight: (theme as Theme).typography.EC_TYPE_3XL.lineHeight,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  })),
  Content: styled(DialogContent)({
    padding: '40px 32px 32px',
    textAlign: 'center',
    backgroundColor: '#FFFFFF',
  }),
  ContentText: styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    marginBottom: '40px',
    maxWidth: '420px',
    margin: '0 auto 40px auto',
  })),
  ButtonContainer: styled(Box)({
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    flexWrap: 'wrap',
    '@media (max-width: 600px)': {
      flexDirection: 'column',
      gap: '12px',
    },
  }),
  PrimaryButton: styled(Button)(({ theme }) => ({
    fontSize: `${(theme as Theme).typography.EC_TYPE_SM.fontSize} !important`,
    fontWeight: 600,
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    textTransform: 'none',
    borderRadius: '.5em !important',
    padding: '.5em 1em !important',
    minWidth: '200px',
    lineHeight: '1.2',
    '&:hover': {
      backgroundColor: '#357ABD',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(74, 144, 226, 0.3)',
    },
    transition: 'all 0.2s ease-in-out',
    '@media (max-width: 600px)': {
      minWidth: '100%',
    },
  })),
  SecondaryButton: styled(Button)(({ theme }) => ({
    fontSize: `${(theme as Theme).typography.EC_TYPE_SM.fontSize} !important`,
    fontWeight: 600,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
    padding: '0.5em 1em !important',
    textTransform: 'none',
    borderRadius: '0.5em !important',
    minWidth: '200px',
    lineHeight: '1.2',
    '&:hover': {
      backgroundColor: 'rgba(74, 144, 226, 0.08)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(74, 144, 226, 0.15)',
    },
    transition: 'all 0.2s ease-in-out',
    '@media (max-width: 600px)': {
      minWidth: '100%',
    },
  })),
};

interface Props {
  open: boolean;
  onClose: () => void;
  onContinueToEscoffier: () => void;
  onStayOnEscoffierGlobal: () => void;
}

const LeavingEscoffierGlobalWarningModal: React.FC<Props> = ({
  open,
  onClose,
  onContinueToEscoffier,
  onStayOnEscoffierGlobal,
}) => {
  const handleContinueClick = (): void => {
    onContinueToEscoffier();
    onClose();
  };

  const handleStayClick = (): void => {
    onStayOnEscoffierGlobal();
    onClose();
  };

  return (
    <Styled.Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="leaving-escoffier-global-title"
      aria-describedby="leaving-escoffier-global-description"
    >
      <Styled.Header>
        <Styled.CloseButton onClick={onClose} aria-label="Close">
          <CloseIcon />
        </Styled.CloseButton>
        <Styled.HeaderTitle 
          variant="EC_TYPE_2XL" 
          id="leaving-escoffier-global-title"
        >
          Leaving EscoffierGlobal.com
        </Styled.HeaderTitle>
      </Styled.Header>
      <Styled.Content>
        <Styled.ContentText 
          variant="EC_TYPE_BASE" 
          id="leaving-escoffier-global-description"
        >
          Please be aware you are transitioning from EscoffierGlobal.com
          to Escoffier.edu to learn more about products and services
          offered through Auguste Escoffier School of Culinary Arts.
        </Styled.ContentText>
        <Styled.ButtonContainer>
          <Styled.PrimaryButton onClick={handleContinueClick}>
            Continue to Escoffier.edu
          </Styled.PrimaryButton>
          <Styled.SecondaryButton onClick={handleStayClick}>
            Stay on EscoffierGlobal.com
          </Styled.SecondaryButton>
        </Styled.ButtonContainer>
      </Styled.Content>
    </Styled.Dialog>
  );
};

export default LeavingEscoffierGlobalWarningModal;
